import { useState } from 'react';

import Image from 'next/image';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

import { HopiIcon } from '@assets/icons';
import { Col, FlexibleDiv } from '@components/formStyles';
import hopi from '@assets/images/hopi-phone.png';
import { Field } from '@components/utils/form';
import HopiNumber from '@components/ui/form/FormattedInputs/HopiNumber';
import FlexContainer from '@components/utils/FlexContainer';
import useBasketInformation from '@hooks/useBasketInformation';
import Modal from '@components/Modal/next/Modal';
import { useDevice } from '@contexts/device';

import ModalFooter from './ModalFooter';
import { HopiModalSchema } from '../../schemas';
import Promotions, { HopiOption } from './Promotions';
import {
  Bold,
  ButtonWrapper,
  Content,
  DynamicDiv,
  HopiWrapper,
  LogoWrapper,
  MainWrapper,
  Paragraph,
  Section,
  StyledSection,
  StyledButton,
  HopiFormRow,
} from './styled';
import { useHopiCodeValidation, useHopiPromotions } from './hooks';
import {
  HopiModalFormData,
  HopiModalProps,
  HopiFlowProps,
  SelectablePromotion,
} from './types';
import { getSelectedPromotionItem } from './utils';

export type { HopiFlowProps };

const staticProps = {
  width: 674,
  title: 'Hopi Hesap Doğrulama',
  promotionTitle: 'Hopi Nedir?',
  promotionDescription:
    'Alışverişin app’i Hopi; zevklerini, alışveriş alışkanlıklarını öğrenir ve istediğin ürünlere kolayca ulaşmanı sağlar. Hiçbir yerde olmayan kampanyalarıyla alışverişlerinde sana Paracık kazandırır.',
};

const staticButtonStyle = {
  height: '100%',
};

const initialValues = {
  hopiCode: '',
};

const shouldFetchPromotions = (promotions: SelectablePromotion[] = []) => {
  if (!promotions.length) return false;
  if (promotions.length === 1 && promotions[0].variation.selected) return false;
  return true;
};

const HopiModal = ({
  isValidated = false,
  onCancel,
  basketId,
  onValidate,
  onValidateError,
  closeModal,
  hotelsIds = [],
  toursIds = [],
  toursPromotions,
  hotelsPromotions,
  onHopiPromotionSelected = () => void 0,
  ...rest
}: HopiModalProps) => {
  const { isMobile } = useDevice();
  const { refetch: refetchBasket, basketInfo } = useBasketInformation();

  const { handleHopiValidate, isValidating } = useHopiCodeValidation({
    onValidate,
    onValidateError,
    closeModal,
    basketId,
    refetchBasket,
  });

  const {
    data: selectableHotelHopiPromotions = hotelsPromotions?.promotions ?? [],
    showPromotions: showPromotionsForHotels,
  } = useHopiPromotions({
    itemsIds: hotelsIds,
    basketInfo,
    type: 'Hotel',
    shouldFetch:
      shouldFetchPromotions(hotelsPromotions?.promotions) && isValidated,
  });

  const {
    data: selectableTourHopiPromotions = toursPromotions?.promotions ?? [],
    showPromotions: showPromotionsForTours,
  } = useHopiPromotions({
    itemsIds: toursIds,
    basketInfo,
    type: 'Tour',
    shouldFetch:
      shouldFetchPromotions(toursPromotions?.promotions) && isValidated,
  });

  const {
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<HopiModalFormData>({
    defaultValues: initialValues,
    resolver: yupResolver(HopiModalSchema),
  });
  const [selectedPromotion, setSelectedPromotion] = useState<{
    promotion?: HopiOption;
    index?: number;
    type?: string;
  }>({});

  const onHotelPromotionSelected = (promotion: string, index?: number) => {
    const selectedPromotionItem = getSelectedPromotionItem(
      promotion,
      allSelectablePromotions,
      'Hotel',
      index
    );

    if (!selectedPromotionItem) return;

    setSelectedPromotion({
      promotion: selectedPromotionItem,
      index,
      type: 'Hotel',
    });
  };

  const onTourPromotionSelected = (promotion: string, index?: number) => {
    const selectedPromotionItem = getSelectedPromotionItem(
      promotion,
      allSelectablePromotions,
      'Tour',
      index
    );

    if (!selectedPromotionItem) return;
    setSelectedPromotion({
      promotion: selectedPromotionItem,
      index,
      type: 'Tour',
    });
  };

  const showPromotions =
    (showPromotionsForHotels || showPromotionsForTours) && isValidated;

  const allSelectablePromotions = [
    {
      title: 'Otel',
      promotions: selectableHotelHopiPromotions,
      handler: onHotelPromotionSelected,
      type: 'Hotel',
    },
    {
      title: 'Tur',
      promotions: selectableTourHopiPromotions,
      handler: onTourPromotionSelected,
      type: 'Tour',
    },
  ];
  return (
    <Modal
      onCancel={onCancel}
      footer={
        showPromotions ? (
          <ModalFooter
            disabled={selectedPromotion.index === undefined}
            closeModal={closeModal}
            refetchBasket={() => {
              refetchBasket();
              onHopiPromotionSelected(selectedPromotion);
            }}
            selectedPromotion={selectedPromotion.promotion}
            basketId={basketInfo?.id}
          />
        ) : null
      }
      borderRadius={8}
      footerStyles={
        isMobile
          ? {
              margin: '0',
            }
          : undefined
      }
      {...staticProps}
      {...rest}
    >
      {showPromotions ? (
        <FlexibleDiv bottom={30}>
          <LogoWrapper data-role='logo'>
            <HopiIcon width={80} />
          </LogoWrapper>

          <FlexContainer direction='column' data-role='promotions-container'>
            <Promotions
              allPromotions={allSelectablePromotions}
              selectedOption={selectedPromotion}
            />
          </FlexContainer>
        </FlexibleDiv>
      ) : (
        <DynamicDiv>
          <form
            onSubmit={handleSubmit((data) => {
              handleHopiValidate(data);
            })}
            style={{ padding: '0 12px 0 20px' }}
          >
            <Content>
              <FlexContainer justifyContent='space-between' gap={32}>
                <MainWrapper>
                  <FlexContainer direction='column'>
                    <LogoWrapper>
                      <HopiIcon width={80} />
                    </LogoWrapper>

                    <Section>
                      <Paragraph>
                        <Bold>Hopi</Bold>
                        'n varsa kampanyalardan yararlanmak ve
                        <Bold> Paracık</Bold>
                        'larınla alışveriş yapmak için aşağıya
                        <Bold> Hopi Kodunu gir.</Bold>
                      </Paragraph>
                    </Section>

                    <HopiFormRow>
                      <Controller
                        control={control}
                        name='hopiCode'
                        render={({ field }) => (
                          <Field
                            Component={HopiNumber}
                            {...field}
                            label='Hopi Kodunu Gir'
                            error={Boolean(errors.hopiCode)}
                          />
                        )}
                      />
                      <ButtonWrapper>
                        <StyledButton
                          style={staticButtonStyle}
                          disabled={isValidating}
                          size='large'
                          type='submit'
                          variant='Primary'
                        >
                          Devam Et
                        </StyledButton>
                      </ButtonWrapper>
                    </HopiFormRow>
                  </FlexContainer>
                </MainWrapper>
              </FlexContainer>
            </Content>
          </form>
          <HopiWrapper>
            <Col>
              <StyledSection>
                <Image width={144} height={291} alt='hopi' src={hopi.src} />
              </StyledSection>
            </Col>
          </HopiWrapper>
        </DynamicDiv>
      )}
    </Modal>
  );
};

export default HopiModal;
